/**
 * One `visibilitychange` subscription shared by every poller and the tab
 * leader — each caller registers a listener; the DOM listener is bound
 * on the first subscription and released with the last.
 */

type VisibilityListener = (visible: boolean) => void;

const listeners = new Set<VisibilityListener>();
let bound = false;

function onVisibilityChange() {
  const visible = !document.hidden;
  // Copy: a listener may unsubscribe (or subscribe) while we iterate.
  for (const listener of [...listeners]) {
    listener(visible);
  }
}

/** True unless the document is hidden (background tab, minimised window). */
export function isDocumentVisible(): boolean {
  return typeof document === 'undefined' || !document.hidden;
}

export function subscribeVisibility(listener: VisibilityListener): () => void {
  listeners.add(listener);
  if (!bound && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
    bound = true;
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && bound) {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      bound = false;
    }
  };
}
